import { Type } from '@angular/core';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { Observable } from 'rxjs';

export interface DynamicForm {
  title: string;
  description?: string;
  status?: string;
  class?: string;
  isHeaderHidden?: Observable<boolean> | boolean;
  orientation?: 'vertical' | 'horizontal';
  grids: DynamicGrid[];
  showLegend?: boolean;
}

export interface DynamicGrid {
  title: string;
  description?: string;
  class?: string;
  isHeaderHidden?: Observable<boolean> | boolean;
  gridItems: DynamicGridItem[];
}


export interface DynamicGridItem {
  fields: DynamicField[];
  ignoreHiddenFields?: Observable<boolean>;
  class?: string;
  isHidden?: boolean | Observable<boolean>;
}

export interface DynamicField<T = any> {
  label?: string;
  labelClass?: string;
  variant: FieldVariant;
  description?: string;
  class?: string;
  error?: Observable<string | string[]>;
  isRequired?: boolean;
  isDisabled?: Observable<boolean>;
  isHidden?: boolean | Observable<boolean>;
  props?: Partial<T>;
}

export interface BaseFieldProps {
  value: Observable<any>;
  onChange: (event: any) => void;
  onBlur?: () => void;
}

export interface TextFieldProps extends BaseFieldProps {
  placeholder?: string;
}

export interface EmailFieldProps extends BaseFieldProps {
  placeholder?: string;
}

export interface TelFieldProps extends BaseFieldProps {
  placeholder?: string;
}

export interface NumberFieldProps extends BaseFieldProps {
  placeholder?: string;
  mode?: 'decimal' | 'currency';
  minFractionDigits?: number;
  maxFractionDigits?: number;
  min?: number;
  max?: number;
}

export interface PasswordFieldProps extends BaseFieldProps {
  placeholder?: string;
  message?: string;
}

export interface DateFieldProps extends BaseFieldProps {
  view: 'date' | 'month' | 'year';
  format?: string;
  minDate?: Date;
  lastDate?: Date;
  showTime?: boolean;
  placeholder?: string;
}

export interface TextareaFieldProps extends BaseFieldProps {
  placeholder?: string;
  rows?: number;
  cols?: number;
  resize?: 'none' | 'allow';
}

export interface SelectOption {
  name?: string;
  code?: string | number;
}

export interface SearchableSelectOption extends SelectOption {
  searchable?: string;
}

export interface SelectFieldProps {
  placeholder?: string;
  options?: SelectOption[] | Observable<SelectOption[]>;
  value?: Observable<SelectOption | undefined>;
  onSelectChange?: (event: SelectOption) => void;
  onBlur?: () => void;
  filterEnabled?: boolean;
  showClear?: boolean;
}

export interface MultiSelectFieldProps {
  placeholder?: string;
  options?: SelectOption[] | Observable<SelectOption[]>;
  value?: Observable<SelectOption[]>;
  onSelectChange?: (event: SelectOption[]) => void;
}

export interface CheckboxFieldProps {
  breakline?: boolean;
  placeholder?: string;
  options?: SelectOption[];
  values?: Observable<string[]>;
  onCheckChange?: (event: string[]) => void;
}

export interface RadioFieldProps {
  name?: string;
  options?: SelectOption[];
  value?: Observable<SelectOption | undefined | string>;
  onChange?: (event: SelectOption) => void;
}

export interface ButtonFieldProps {
  label?: string;
  class?: string;
  icon?: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast';
  onClick?: () => void;
}

export interface SwitchFieldProps {
  placeholder?: string;
  checked?: Observable<boolean>;
  onChange?: (event: ToggleSwitchChangeEvent) => void;
}

export interface EditorFieldProps extends BaseFieldProps {
  placeholder?: string;
  height?: string;
}

export interface FileRepresentation {
  id?: string | number;
  alfresco_id?: string;
  filename?: string;
  size?: number;
  extension?: string;
}

export interface FileFieldProps {
  // p-fileuploader
  mode: 'basic' | 'advanced';
  accept?: string;
  maxFileSize?: number;
  multiple?: boolean;
  value: Observable<File[]>;
  onFileSelect?: (event: FileSelectEvent) => void;
  onFileRemoved?: (event: FileRemoveEvent) => void;
  showUploadButton?: boolean;
  chooseLabel?: string;
  cancelLabel?: string;
  existingLabel?: string;
  existingFileHeader?: boolean;
  // sm-filelist
  files?: Observable<FileRepresentation[]>;
  onFileRepresentationChange?: (event: any) => void;
  openFile: (file: FileRepresentation) => void;
  onFileDownLoad?: (event: any) => void;
  onAllRemoved?: () => void;
  setResetMethod?: (resetFn: () => void) => void;
  message?: string;
}

export interface ColorSelectOption {
  originalEvent: { isTrusted: boolean };
  value: string;
}

export interface ColorFieldProps {
  value: Observable<string>;
  onChange: (event: ColorSelectOption) => void;
}

export interface TextGroupsFieldProps {
  singular?: string;
  value: Observable<string[]>;
  onChange: (event: string[]) => void;
}

export interface CustomFieldProps {
  component?: Type<any>;
  [key: string]: any;
  injectKeys?: string[];
}

export enum FieldVariant {
  TEXT = 'text',
  EMAIL = 'email',
  TEL = 'tel',
  NUMBER = 'number',
  PASSWORD = 'password',
  DATE = 'date',
  TEXTAREA = 'textarea',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  SWITCH = 'switch',
  FILE = 'file',
  EDITOR = 'editor',
  COLOR = 'color',
  TEXT_GROUPS = 'text-groups',
  CUSTOM = 'custom',
  EMPTY = 'empty',
  BUTTON = 'button',
}
